import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  effect,
  input,
  output,
  signal
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { SafeUrl } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { EMPTY, catchError, distinctUntilChanged, filter, combineLatest, map } from 'rxjs';
import { ImageLoaderProvider } from '../../../services/image-loader-provider.service';
import { SpinnerComponent } from '../../misc/spinner/spinner.component';
import { TooltipDirective } from '../../overlay/tooltip/tooltip.directive';

export enum ImageLoadingStatus {
  COMPLETED = 'COMPLETED',
  PENDING = 'PENDING',
  INVALID = 'INVALID',
  ERROR = 'ERROR',
}

interface ImagePayload {
  status: ImageLoadingStatus;
  src?: SafeUrl;
}

@Component({
  selector: 'osp-image',
  standalone: true,
  templateUrl: './image.component.html',
  styleUrls: ['./image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, SpinnerComponent, TranslateModule, TooltipDirective],
})
export class ImageComponent {
  @HostBinding('class.osp-image') protected hostCssClassEnabled = true;
  src = input('');
  showSpinner = input(true);
  altText = input('');
  directImage = input(false);
  customerId = input<string | undefined>(undefined);

  loading = output<boolean>();

  protected readonly notFoundSvgIcon = 'assets/images/common/image-question.svg';
  protected readonly status = ImageLoadingStatus;
  protected readonly imagePayload = signal<ImagePayload | null>(null);

  private imagePayloadChanges = effect(() => {
    this.loading.emit(this.imagePayload()?.status === ImageLoadingStatus.PENDING);
  });

  constructor(private fileLoaderService: ImageLoaderProvider) {
      this.initListenerSrcChanges();
  }

  private initListenerSrcChanges(): void {
    combineLatest([
      toObservable(this.src),
      toObservable(this.directImage)
    ])
      .pipe(
        filter(([, directImage]) => !directImage),
        map(([src]) => src),
        filter(Boolean), distinctUntilChanged()
      )
      .subscribe((imageKey) => this.loadImage(imageKey));
  }

  private loadImage(imageKey: string): void {
    if (!this.isImageKeyValid(imageKey)) {
      this.imagePayload.set({ status: ImageLoadingStatus.INVALID });
      return;
    }

    this.imagePayload.set({ status: ImageLoadingStatus.PENDING });
    this.fileLoaderService
      .getImage(imageKey, this.customerId())
      .pipe(
        catchError(() => {
          this.imagePayload.set({ status: ImageLoadingStatus.ERROR });
          return EMPTY;
        }),
      )
      .subscribe((src) => {
        this.imagePayload.set({ status: ImageLoadingStatus.COMPLETED, src });
      });
  }

  private isImageKeyValid(imageKey: string): boolean {
    return typeof imageKey === 'string' && Boolean(imageKey);
  }
}
